import { useState } from 'react';
import { Columns, Column, Inline, LinkButton, breakpoints } from '@etg/wings';
import { ExpandableContent } from '@eti/components';
import { ConnectedComboBox, ConnectedInput } from '@eti/form';
import { useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { constants, persistInputData } from '@eti/utils';
import orderBy from 'lodash/orderBy';
import { CaretDown } from '@phosphor-icons/react';
import { FormSection } from 'redux-form';
import { FREQUENT_FLYER, FREQUENT_FLYER_CARD_NUMBER, FREQUENT_FLYER_CARD_TYPE } from '../../utils/constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  MOUSE_FLOW_CLASS_NAMES
} = constants;
const toggleStyles = css`
  align-items: center;
  display: flex;
  font-size: 0.875rem;
  gap: 10px;

  @media (min-width: ${breakpoints._768}) {
    font-size: 1rem;
  }
`;
const topPaddingStyles = css`
  padding-top: 24px;
`;
const toggleIconStyles = open => css`
  transform: rotateX(${open ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;
const normalizeCardType = selectedOption => {
  var _selectedOption$value;
  return (_selectedOption$value = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) !== null && _selectedOption$value !== void 0 ? _selectedOption$value : '';
};
const normalizeCardNumber = (value, previousValue) => {
  if (!/^[0-9a-zA-Z]*$/.test(value)) {
    return previousValue;
  }
  return value.toUpperCase();
};
const columnWidth = {
  [breakpoints._0]: '4/4',
  [breakpoints._560]: '2/4'
};
const FrequentFlyer = _ref => {
  let {
    cardTypes,
    dataTestId,
    form,
    id,
    isOpenByDefault,
    travelerNumber
  } = _ref;
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const {
    isSnowplowTrackingEnabled,
    sendTrackEvent
  } = useSnowplowTracking();
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const [hasFrequentFlyerCardTypeChanged, setHasFrequentFlyerCardTypeChanged] = useState(false);
  const sortedCardTypes = orderBy(cardTypes, 'label', 'asc');
  const handleToggle = () => {
    setIsOpen(prevIsOpen => !prevIsOpen);
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Traveler Details',
        action: 'Click',
        label: 'Expand Add Frequent Flyer',
        value: travelerNumber
      });
    }
  };
  const handleOnCardTypeBlur = option => {
    if (p('IbeClient.TravelerDetails.PersistFormData.Enabled')) {
      persistInputData(form, `traveler-${id}-${FREQUENT_FLYER}-${FREQUENT_FLYER_CARD_TYPE}`)(option === null || option === void 0 ? void 0 : option.value);
    }
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Traveler Details',
        action: 'Select',
        label: 'Frequent flyer card type',
        property: option === null || option === void 0 ? void 0 : option.label,
        value: travelerNumber
      });
    }
  };
  const handleOnCardNumberBlur = event => {
    if (p('IbeClient.TravelerDetails.PersistFormData.Enabled')) {
      persistInputData(form, `traveler-${id}-${FREQUENT_FLYER}-${FREQUENT_FLYER_CARD_NUMBER}`)(event.target.value);
    }
    if (isSnowplowTrackingEnabled && !hasFrequentFlyerCardTypeChanged) {
      sendTrackEvent({
        category: 'Traveler Details',
        action: 'Input',
        label: 'Frequent flyer card number',
        value: travelerNumber
      });
      setHasFrequentFlyerCardTypeChanged(true);
    }
  };
  const formatCardType = option => {
    var _sortedCardTypes$find;
    return (_sortedCardTypes$find = sortedCardTypes.find(card => card.value === option)) !== null && _sortedCardTypes$find !== void 0 ? _sortedCardTypes$find : '';
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(Inline, {
      align: "center",
      children: /*#__PURE__*/_jsxs(LinkButton, {
        "aria-controls": `frequentFlyerForm-${id}`,
        "aria-expanded": isOpen,
        className: toggleStyles,
        "data-testid": dataTestId,
        onClick: handleToggle,
        children: [/*#__PURE__*/_jsx("span", {
          children: t('TravelerForm.FrequentFlyer.Label')
        }), /*#__PURE__*/_jsx(CaretDown, {
          className: toggleIconStyles(isOpen),
          size: "1.5em"
        })]
      })
    }), /*#__PURE__*/_jsx(ExpandableContent, {
      isExpanded: isOpen,
      children: /*#__PURE__*/_jsx(FormSection, {
        name: FREQUENT_FLYER,
        children: /*#__PURE__*/_jsxs(Columns, {
          className: topPaddingStyles,
          "data-testid": "traveler-details-frequentFlyer-wrapper",
          id: `frequentFlyerForm-${id}`,
          spacing: 24,
          children: [/*#__PURE__*/_jsx(Column, {
            width: columnWidth,
            children: /*#__PURE__*/_jsx(ConnectedComboBox, {
              className: MOUSE_FLOW_CLASS_NAMES,
              "data-testid": `travelerDetails-FrequentFlyerCardType-${id}-dropdown`,
              format: formatCardType,
              label: t('TravelerForm.FrequentFlyerCardType.Placeholder'),
              name: FREQUENT_FLYER_CARD_TYPE,
              normalize: normalizeCardType,
              onBlur: handleOnCardTypeBlur,
              options: sortedCardTypes,
              placeholder: t('TravelerForm.FrequentFlyerCardType.Label')
            })
          }), /*#__PURE__*/_jsx(Column, {
            width: columnWidth,
            children: /*#__PURE__*/_jsx(ConnectedInput, {
              autoCorrect: "off",
              className: MOUSE_FLOW_CLASS_NAMES,
              "data-testid": `traveler-frequent-flyer-card-number-${id}-input`,
              label: t('TravelerForm.FrequentFlyerCardNumber.Label'),
              name: FREQUENT_FLYER_CARD_NUMBER,
              normalize: normalizeCardNumber,
              onBlur: handleOnCardNumberBlur,
              placeholder: t('Input.FrequentFlyer.CardNumber.Placeholder'),
              spellCheck: "false"
            })
          })]
        })
      })
    })]
  });
};
export default FrequentFlyer;